import React from 'react';
import { InfoCircleOutlined } from '@ant-design/icons';
import { FormInstance } from 'antd';
import cx from 'classnames';
import { useTranslation } from 'react-i18next';
import { expiryOptions } from '@app/pages/b2b/QuestionsLibrary/pages/constants';
import { getLanguageOptions } from '@app/utils';
import { usePermissions } from '@data/hooks';
import { AnswerType, QuestionDetailsView, QuestionTranslationView } from '@data/sdk/match';
import { Language, Namespace } from '@data/utils/enums';
import {
  AntdCol,
  AntdForm,
  AntdInput,
  AntdRadio,
  AntdRow,
  AntdSegmented,
  AntdSelect,
  AntdSkeleton,
  AntdSpace,
  AntdTooltip,
  AntdTypography,
  Button,
} from '@ui/components';
import { QuestionFormValues } from '../../hooks';
import { TypeTag } from './components';
import { getQuestionTypeOptions } from './constants';
import { getQuestionVisibilityOptions } from './utils';
import './styles.css';

type QuestionFormProps = {
  currentLanguage: Language;
  form: FormInstance<QuestionFormValues>;
  handleEditQuestion: (values: QuestionFormValues) => Promise<void>;
  handleResetForm: () => void;
  hasDependencies?: boolean;
  initialValues: QuestionFormValues;
  isFormDirty: boolean;
  isQuestionLoading: boolean;
  question?: QuestionDetailsView;
  setCurrentLanguage: React.Dispatch<React.SetStateAction<Language>>;
  translations?: QuestionTranslationView[];
};

const { Text } = AntdTypography;

export const QuestionForm = ({
  form,
  question,
  isFormDirty,
  translations,
  initialValues,
  currentLanguage,
  hasDependencies,
  isQuestionLoading,
  handleEditQuestion,
  setCurrentLanguage,
  handleResetForm,
}: QuestionFormProps) => {
  const { t } = useTranslation([Namespace.ADMIN]);
  const { IS_REVIEWER } = usePermissions();

  return (
    <AntdForm
      form={form}
      layout="vertical"
      autoComplete="off"
      requiredMark={false}
      onFinish={handleEditQuestion}
      initialValues={initialValues}
      scrollToFirstError={{ behavior: 'smooth' }}
      className="w-full"
      disabled={IS_REVIEWER || isQuestionLoading}
    >
      <AntdRow gutter={24} className="items-center">
        <AntdCol lg={14}>
          {Object.values(Language).map((lang) => (
            <AntdForm.Item
              key={`text.${lang}`}
              name={['text', `${lang}`]}
              className={cx(lang !== currentLanguage && 'hidden')}
              label={
                <AntdTooltip
                  title={
                    currentLanguage !== Language.EN
                      ? t('question_page.details.form.translation_tooltip')
                      : null
                  }
                  className="flex gap-1"
                  placement="right"
                >
                  <Text>{t('question_page.details.form.question')}</Text>
                  {currentLanguage !== Language.EN && !IS_REVIEWER && (
                    <InfoCircleOutlined className="text-primary-600" />
                  )}
                </AntdTooltip>
              }
              rules={[
                ({ getFieldValue }) => ({
                  validator() {
                    if (!getFieldValue(['text', Language.EN])) {
                      return Promise.reject(
                        new Error(t('patient:validations.default_translation')),
                      );
                    }
                    return Promise.resolve();
                  },
                }),
              ]}
            >
              <AntdInput />
            </AntdForm.Item>
          ))}
        </AntdCol>

        <AntdCol>
          <AntdSegmented
            className="lang-segment mt-1 bg-base-200 w-full"
            options={getLanguageOptions(translations)}
            value={currentLanguage}
            onChange={(v) => setCurrentLanguage(v as Language)}
          />
        </AntdCol>
      </AntdRow>
      <AntdRow gutter={24}>
        <AntdCol lg={5}>
          <AntdForm.Item
            name="questionType"
            label={t('question_page.details.form.type')}
            className="mb-4"
          >
            <AntdSelect
              className="w-full questionType"
              disabled={IS_REVIEWER}
              options={getQuestionTypeOptions()}
            />
          </AntdForm.Item>
        </AntdCol>

        {question?.answerType === AnswerType.Number && (
          <AntdCol lg={4}>
            <AntdForm.Item
              key="unit"
              name="unit"
              label={<Text>{t('question_page.details.form.unit')}</Text>}
            >
              <AntdInput maxLength={255} />
            </AntdForm.Item>
          </AntdCol>
        )}

        <AntdCol lg={5}>
          <AntdTooltip
            title={
              hasDependencies ? t('question_page.details.form.block_edit_visibility_msg') : null
            }
          >
            <AntdForm.Item
              name="visibilityType"
              className="m-0"
              label={t('question_page.details.form.visibility')}
              rules={[
                {
                  validator: (_, val) =>
                    val
                      ? Promise.resolve()
                      : Promise.reject(t('patient:validations.please_select')),
                },
              ]}
            >
              <AntdRadio.Group disabled={hasDependencies || IS_REVIEWER}>
                {getQuestionVisibilityOptions().map((el) => (
                  <AntdRadio key={el.value} value={el.value}>
                    {el.label}
                  </AntdRadio>
                ))}
              </AntdRadio.Group>
            </AntdForm.Item>
          </AntdTooltip>
        </AntdCol>

        <AntdCol lg={4}>
          <Text className="block mb-3">{t('question_page.details.form.answer_type')}</Text>
          {isQuestionLoading ? (
            <AntdSkeleton.Input className="h-5 w-24 min-w-0" />
          ) : (
            <TypeTag tagType="answer_types" type={question?.answerType} />
          )}
        </AntdCol>

        <AntdCol lg={4}>
          <Text className="block mb-3">{t('question_page.details.form.generation_type')}</Text>
          {isQuestionLoading ? (
            <AntdSkeleton.Input className="h-5 w-24 min-w-0" />
          ) : (
            <TypeTag tagType="generation_types" type={question?.generationType} />
          )}
        </AntdCol>
      </AntdRow>

      <AntdRow gutter={24}>
        <AntdCol lg={5}>
          <AntdForm.Item name="expiry" label={t('question_page.common.expiry')}>
            <AntdSelect options={expiryOptions()} />
          </AntdForm.Item>
        </AntdCol>
      </AntdRow>

      {!IS_REVIEWER && (
        <div className="fixed bottom-0 left-0 w-full py-2 px-0 sm:px-6 border-t border-base-200 border-solid border-x-0 border-b-0 bg-base-100 z-10 text-end">
          <AntdSpace>
            <Button type="primary" htmlType="submit" disabled={!isFormDirty}>
              {t('patient:common.save_changes')}
            </Button>

            <Button onClick={handleResetForm} disabled={!isFormDirty}>
              {t('patient:common.cancel')}
            </Button>
          </AntdSpace>
        </div>
      )}
    </AntdForm>
  );
};
